/* ----------------- Hooks --------------- */
import { useAppTheme } from "@hooks/useAppTheme";

/* ----------------- Components --------------- */
import ProductIcon from "components/ProductIcon";
import RowStats from "components/RowStats";
import { Card, Text } from "react-native-paper";

/* ----------------- Icons --------------- */
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppStore } from "@hooks/useAppStore";
import { useDevice } from "@hooks/useDevice";
import type { PracticeTestExam } from "@memorang/types/src/tag";
import Box from "@memorang/ui/src/components/Box";
import DonutChart from "@memorang/ui/src/components/DonutChart";
import QuickActionChipsSkeleton from "@memorang/ui/src/components/QuickActionsChipsSkeleton";
import { DonutChartSkeleton } from "@memorang/ui/src/components/Skeletons/DonutChartSkeleton";
import { StatsSkeleton } from "@memorang/ui/src/components/Skeletons/StatsSkeleton";
import { TitleSubtitleSkeleton } from "@memorang/ui/src/components/Skeletons/TitleSubtitleSkeleton";
import { SetExamDate } from "components/ExamContextListItem";
import DiagnosticItemsContainer from "containers/DiagnosticItemsContainer";
import QuickActionsContainer from "containers/QuickActionsContainer";
import type { HeaderSectionData } from "../../types";
interface Props {
	headerData?: HeaderSectionData;
	exams?: PracticeTestExam[];
	hasAccess?: boolean;
	handleShowPurchase: () => void;
}

const ExamInfoSection = ({
	headerData,
	exams,
	hasAccess,
	handleShowPurchase,
}: Props) => {
	const theme = useAppTheme();
	const showDiagnostic = useAppStore((state) => state.app.showDiagnostic);

	const allowChangeExam = useAppStore((state) => state.exam.allowChangeExam);

	const { isMobile } = useDevice();

	const { isSmallScreen } = useDevice();

	return (
		<Box gap={16}>
			<Card
				style={{
					overflow: "hidden",
					borderRadius: 0,
				}}
				accessibilityLabel={`Exam Information section: Exam Name - ${headerData?.examName}. Progress - ${headerData?.headerStats.map((stat) => `${stat.label} ${stat.value}`).join(". ")}. This section includes a button to switch exams or set the exam date.`}
				accessible
			>
				<Card.Content>
					<Box
						width={"100%"}
						flexDirection={isSmallScreen ? "column" : "row"}
						gap={16}
						justifyContent="space-between"
					>
						<Box
							alignItems="center"
							flexDirection={isSmallScreen ? "column" : "row"}
							gap={16}
						>
							{headerData ? (
								<DonutChart
									distribution={headerData.distribution}
									radius={61}
									colorScale={headerData.masteryProgressColorScale}
									progressData={headerData.masteryProgressData}
									innerRadius={50}
								>
									{headerData.examIcon ? (
										<ProductIcon
											image={headerData.examIcon}
											size={64}
											showBorder={false}
										/>
									) : (
										<Icon
											name="file-document-outline"
											size={60}
											color={theme.colors.onSurfaceVariant}
										/>
									)}
								</DonutChart>
							) : (
								<DonutChartSkeleton
									size={121}
									isSmallScreen={isSmallScreen}
									avatarStyle={{
										marginLeft: -8,
									}}
								/>
							)}
							{allowChangeExam && !isMobile && (
								<Box
									alignItems={isSmallScreen ? "center" : "flex-start"}
									gap={8}
								>
									{headerData ? (
										<>
											<Text
												variant="titleMedium"
												numberOfLines={2}
												style={{
													maxWidth: isSmallScreen ? "100%" : 300,
												}}
											>
												{headerData.examName}
											</Text>

											<SetExamDate
												examDateLabel={headerData.examStatusText}
												textColor={theme.colors.primary}
												examDate={headerData.examDate}
											/>
										</>
									) : (
										<TitleSubtitleSkeleton />
									)}
								</Box>
							)}
						</Box>
						{headerData ? (
							<RowStats
								showBorder={false}
								stats={headerData.headerStats}
								flexGrow={1}
							/>
						) : (
							<StatsSkeleton isSmallScreen={isSmallScreen} />
						)}
					</Box>
				</Card.Content>
			</Card>
			{headerData ? <QuickActionsContainer /> : <QuickActionChipsSkeleton />}
			{showDiagnostic || allowChangeExam ? null : (
				<DiagnosticItemsContainer
					exams={exams}
					hasAccess={hasAccess}
					isDataLoading={!headerData}
					handleShowPurchase={handleShowPurchase}
				/>
			)}
		</Box>
	);
};

export default ExamInfoSection;
